/**
 * HR Engine - Page Mes Évaluations (Vue Employé)
 * Permet à l'employé de voir ses campagnes et faire son auto-évaluation
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Target,
  Calendar,
  Loader2,
  CheckCircle2,
  Clock,
  FileText,
  Send,
  AlertTriangle,
  Star,
  ChevronRight,
  User,
  Building2,
  Award,
  TrendingUp,
  MessageSquare,
  Download,
  Eye,
  Edit3,
  Lock,
  ArrowLeft
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-slate-100 text-slate-700', icon: Clock },
  self_eval_pending: { label: 'Auto-évaluation en attente', color: 'bg-yellow-100 text-yellow-700', icon: Edit3 },
  self_eval_done: { label: 'Auto-évaluation terminée', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  manager_eval_pending: { label: 'Éval. manager en attente', color: 'bg-orange-100 text-orange-700', icon: Clock },
  manager_eval_done: { label: 'Évalué par manager', color: 'bg-purple-100 text-purple-700', icon: CheckCircle2 },
  n2_validation_pending: { label: 'Validation N+2 en attente', color: 'bg-indigo-100 text-indigo-700', icon: Clock },
  validated: { label: 'Validé', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  completed: { label: 'Complété', color: 'bg-emerald-100 text-emerald-700', icon: Award }
};

// Performance level colors
const PERFORMANCE_COLORS = {
  high_performer: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'High Performer' },
  performer: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Performer' },
  non_performer: { bg: 'bg-red-100', text: 'text-red-700', label: 'Non Performer' }
};

export default function MyEvaluationsEmployeePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { evaluationId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [activeEvaluation, setActiveEvaluation] = useState(null);
  
  // Self-evaluation form state
  const [selfEvalForm, setSelfEvalForm] = useState({
    objectiveScores: [],
    globalComment: '',
    achievements: '',
    challenges: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState(false);

  useEffect(() => {
    fetchMyEvaluations();
  }, []);

  useEffect(() => {
    if (evaluationId && evaluations.length > 0) {
      const eval_ = evaluations.find(e => e.id === evaluationId);
      if (eval_) {
        openEvaluation(eval_);
      }
    }
  }, [evaluationId, evaluations]);

  const fetchMyEvaluations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/performance-eval/my-evaluations');
      setEvaluations(response.data.evaluations || []);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      toast.error('Erreur lors du chargement des évaluations');
    } finally {
      setLoading(false);
    }
  };

  const openEvaluation = async (evaluation) => {
    try {
      const response = await api.get(`/performance-eval/evaluations/${evaluation.id}`);
      const fullEval = response.data;
      setActiveEvaluation(fullEval);
      
      // Initialize form with existing data or empty
      const objectives = fullEval.objectives || [];
      setSelfEvalForm({
        objectiveScores: objectives.map(obj => ({
          objectiveId: obj.id,
          score: obj.selfScore || 50,
          comment: obj.selfComment || ''
        })),
        globalComment: fullEval.selfEvaluation?.globalComment || '',
        achievements: fullEval.selfEvaluation?.achievements || '',
        challenges: fullEval.selfEvaluation?.challenges || ''
      });
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'évaluation');
    }
  };

  const downloadPDF = async (evaluationId, e) => {
    e?.stopPropagation();
    try {
      const response = await api.get(
        `/performance-eval/evaluations/${evaluationId}/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluation-${evaluationId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Lire le detail même quand la réponse est un blob d'erreur
      let msg = 'Impossible de télécharger le PDF';
      try {
        const text = await err.response?.data?.text?.();
        if (text) {
          const parsed = JSON.parse(text);
          msg = parsed?.detail || msg;
        }
      } catch {
        msg = err.response?.data?.detail || msg;
      }
      toast.error(msg);
    }
  };

  const updateObjectiveScore = (objectiveId, field, value) => {
    setSelfEvalForm(prev => ({
      ...prev,
      objectiveScores: prev.objectiveScores.map(os => 
        os.objectiveId === objectiveId ? { ...os, [field]: value } : os
      )
    }));
  };

  const handleSubmitSelfEval = async () => {
    try {
      setSubmitting(true);
      
      // Validate that all objectives have scores
      const missingScores = selfEvalForm.objectiveScores.filter(os => os.score === undefined || os.score === null);
      if (missingScores.length > 0) {
        toast.error('Veuillez noter tous les objectifs');
        return;
      }
      
      await api.post(`/performance-eval/evaluations/${activeEvaluation.id}/self-eval`, selfEvalForm);
      
      toast.success('Auto-évaluation soumise avec succès !');
      setSubmitConfirm(false);
      setActiveEvaluation(null);
      fetchMyEvaluations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateAverageScore = () => {
    if (!activeEvaluation?.objectives?.length) return 0;
    const objectives = activeEvaluation.objectives;
    const totalWeight = objectives.reduce((sum, obj) => sum + (obj.weight || 20), 0);
    const weightedScore = selfEvalForm.objectiveScores.reduce((sum, os) => {
      const obj = objectives.find(o => o.id === os.objectiveId);
      return sum + (os.score * ((obj?.weight || 20) / totalWeight));
    }, 0);
    return Math.round(weightedScore);
  };

  const canEdit = activeEvaluation?.status === 'self_eval_pending';

  if (loading) {
    return (
      <DashboardLayout title="Mes Évaluations">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  // If viewing a specific evaluation
  if (activeEvaluation) {
    const status = STATUS_CONFIG[activeEvaluation.status] || STATUS_CONFIG.pending;
    const StatusIcon = status.icon;
    const avgScore = calculateAverageScore();
    
    return (
      <DashboardLayout title="Mon Évaluation">
        <div className="space-y-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <Button 
                variant="ghost" 
                className="mb-2 -ml-2"
                onClick={() => setActiveEvaluation(null)}
              >
                <ChevronRight className="h-4 w-4 mr-2 rotate-180" />
                Retour aux évaluations
              </Button>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Target className="h-7 w-7 text-indigo-500" />
                {activeEvaluation.campaign?.name || 'Évaluation'}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <Badge className={status.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {status.label}
                </Badge>
                {activeEvaluation.finalScore && (
                  <Badge className={PERFORMANCE_COLORS[activeEvaluation.performanceLevel]?.bg + ' ' + PERFORMANCE_COLORS[activeEvaluation.performanceLevel]?.text}>
                    {activeEvaluation.finalScore}/100 - {PERFORMANCE_COLORS[activeEvaluation.performanceLevel]?.label}
                  </Badge>
                )}
              </div>
            </div>
            
            {activeEvaluation.status === 'completed' && (
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
            )}
          </div>

          {/* Info Card */}
          <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
            <CardContent className="py-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-indigo-500" />
                  <div>
                    <p className="text-xs text-slate-500">Employé</p>
                    <p className="font-medium text-sm">
                      {activeEvaluation.employeeSnapshot?.firstName} {activeEvaluation.employeeSnapshot?.lastName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-indigo-500" />
                  <div>
                    <p className="text-xs text-slate-500">Poste</p>
                    <p className="font-medium text-sm">{activeEvaluation.employeeSnapshot?.jobTitle || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-500" />
                  <div>
                    <p className="text-xs text-slate-500">Période</p>
                    <p className="font-medium text-sm">
                      {activeEvaluation.campaign?.periodStart && new Date(activeEvaluation.campaign.periodStart).toLocaleDateString('fr-FR')} - 
                      {activeEvaluation.campaign?.periodEnd && new Date(activeEvaluation.campaign.periodEnd).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-indigo-500" />
                  <div>
                    <p className="text-xs text-slate-500">Score actuel</p>
                    <p className="font-bold text-lg text-indigo-600">{avgScore}/100</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          {canEdit && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-900">Instructions</p>
                    <p className="text-sm text-amber-700">
                      Évaluez votre performance sur chaque objectif en attribuant un score de 0 à 100.
                      Ajoutez un commentaire pour justifier votre auto-évaluation.
                      Une fois soumise, votre évaluation sera transmise à votre manager.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Objectives */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-500" />
              Mes Objectifs ({activeEvaluation.objectives?.length || 0})
            </h2>
            
            {activeEvaluation.objectives?.length === 0 ? (
              <Card className="py-8 text-center">
                <CardContent>
                  <Target className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500">Aucun objectif défini pour cette évaluation</p>
                </CardContent>
              </Card>
            ) : (
              activeEvaluation.objectives?.map((objective, index) => {
                const scoreData = selfEvalForm.objectiveScores.find(os => os.objectiveId === objective.id) || {};
                const score = scoreData.score || 0;
                
                return (
                  <Card key={objective.id} className="border-l-4 border-l-indigo-500">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-indigo-600">
                              Objectif {index + 1}
                            </Badge>
                            <Badge className="bg-slate-100 text-slate-700">
                              {objective.weight || 20}%
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-slate-900">{objective.title}</h3>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-indigo-600">{score}</p>
                          <p className="text-xs text-slate-500">/100</p>
                        </div>
                      </div>
                      
                      {objective.description && (
                        <p className="text-sm text-slate-600 mb-3">{objective.description}</p>
                      )}
                      
                      {objective.indicators && (
                        <div className="text-sm bg-slate-50 p-2 rounded mb-3">
                          <span className="font-medium">Indicateurs: </span>
                          {objective.indicators}
                        </div>
                      )}
                      
                      {/* Self-evaluation form */}
                      <div className="space-y-3 pt-3 border-t">
                        <div>
                          <Label className="text-sm font-medium flex items-center justify-between">
                            <span>Mon auto-évaluation</span>
                            <span className={`text-lg font-bold ${
                              score >= 85 ? 'text-emerald-600' : 
                              score >= 60 ? 'text-blue-600' : 'text-red-600'
                            }`}>
                              {score}/100
                            </span>
                          </Label>
                          <Slider
                            value={[score]}
                            onValueChange={([v]) => updateObjectiveScore(objective.id, 'score', v)}
                            min={0}
                            max={100}
                            step={1}
                            disabled={!canEdit}
                            className="my-2"
                          />
                          <div className="flex justify-between text-xs text-slate-400">
                            <span>0 - Non atteint</span>
                            <span>50 - Partiellement</span>
                            <span>100 - Dépassé</span>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-sm">Mon commentaire</Label>
                          <Textarea
                            value={scoreData.comment || ''}
                            onChange={(e) => updateObjectiveScore(objective.id, 'comment', e.target.value)}
                            placeholder="Décrivez vos réalisations et justifiez votre score..."
                            rows={2}
                            disabled={!canEdit}
                          />
                        </div>
                        
                        {/* Manager evaluation display (if available) */}
                        {objective.managerScore !== null && objective.managerScore !== undefined && (
                          <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-purple-900">Évaluation Manager</span>
                              <Badge className="bg-purple-100 text-purple-700">{objective.managerScore}/100</Badge>
                            </div>
                            {objective.managerComment && (
                              <p className="text-sm text-purple-700">{objective.managerComment}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          {/* General comments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-slate-500" />
                Commentaires généraux
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Mes principales réalisations</Label>
                <Textarea
                  value={selfEvalForm.achievements}
                  onChange={(e) => setSelfEvalForm({...selfEvalForm, achievements: e.target.value})}
                  placeholder="Décrivez vos principales réalisations sur cette période..."
                  rows={3}
                  disabled={!canEdit}
                />
              </div>
              
              <div>
                <Label>Défis rencontrés</Label>
                <Textarea
                  value={selfEvalForm.challenges}
                  onChange={(e) => setSelfEvalForm({...selfEvalForm, challenges: e.target.value})}
                  placeholder="Décrivez les défis ou difficultés rencontrés..."
                  rows={3}
                  disabled={!canEdit}
                />
              </div>
              
              <div>
                <Label>Commentaire global</Label>
                <Textarea
                  value={selfEvalForm.globalComment}
                  onChange={(e) => setSelfEvalForm({...selfEvalForm, globalComment: e.target.value})}
                  placeholder="Ajoutez un commentaire général sur votre performance..."
                  rows={3}
                  disabled={!canEdit}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit button */}
          {canEdit && (
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setActiveEvaluation(null)}>
                Enregistrer comme brouillon
              </Button>
              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => setSubmitConfirm(true)}
              >
                <Send className="w-4 h-4 mr-2" />
                Soumettre mon auto-évaluation
              </Button>
            </div>
          )}

          {/* Actions when completed */}
          {isCompleted && (
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setActiveEvaluation(null)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <Button 
                variant="outline"
                onClick={() => downloadPDF(activeEvaluation.id)}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
            </div>
          )}

          {/* Submit confirmation dialog */}
          <AlertDialog open={submitConfirm} onOpenChange={setSubmitConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmer la soumission</AlertDialogTitle>
                <AlertDialogDescription>
                  Êtes-vous sûr de vouloir soumettre votre auto-évaluation ?
                  <br /><br />
                  <strong>Une fois soumise, vous ne pourrez plus la modifier.</strong>
                  <br /><br />
                  Votre score actuel: <strong className="text-indigo-600">{avgScore}/100</strong>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleSubmitSelfEval}
                  disabled={submitting}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Confirmer la soumission
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </DashboardLayout>
    );
  }

  // List of evaluations
  return (
    <DashboardLayout title="Mes Évaluations">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Target className="h-7 w-7 text-indigo-500" />
            Mes Évaluations de Performance
          </h1>
          <p className="text-slate-500 mt-1">
            Consultez et complétez vos évaluations de performance
          </p>
        </div>

        {/* Evaluations List */}
        {evaluations.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Aucune évaluation en cours
              </h3>
              <p className="text-slate-500">
                Vous serez notifié lorsqu'une campagne d'évaluation sera lancée.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {evaluations.map((evaluation) => {
              const status = STATUS_CONFIG[evaluation.status] || STATUS_CONFIG.pending;
              const StatusIcon = status.icon;
              const canSelfEval = evaluation.status === 'self_eval_pending';
              const wasReturned = canSelfEval && evaluation.returnedToSelf;
              const isCompleted = ['validated', 'completed'].includes(evaluation.status);
              
              return (
                <Card 
                  key={evaluation.id}
                  className={`hover:shadow-md transition-all cursor-pointer ${
                    wasReturned ? 'border-orange-300 bg-orange-50/40'
                    : canSelfEval ? 'border-yellow-300 bg-yellow-50/30' : ''
                  }`}
                  onClick={() => openEvaluation(evaluation)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {evaluation.campaign?.name || 'Évaluation'}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <Badge className={wasReturned ? 'bg-orange-100 text-orange-700' : status.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {wasReturned ? 'À reprendre' : status.label}
                          </Badge>
                        </div>
                      </div>
                      {evaluation.finalScore && (
                        <div className="text-right">
                          <p className="text-2xl font-bold text-indigo-600">{evaluation.finalScore}</p>
                          <p className="text-xs text-slate-500">/100</p>
                        </div>
                      )}
                    </div>

                    {wasReturned && evaluation.selfEvalReturnReason && (
                      <div className="mb-3 rounded-md bg-orange-100/70 border border-orange-200 px-3 py-2">
                        <p className="text-xs font-medium text-orange-900">Motif du renvoi :</p>
                        <p className="text-xs text-orange-800 mt-0.5 whitespace-pre-wrap break-words line-clamp-3">
                          {evaluation.selfEvalReturnReason}
                        </p>
                      </div>
                    )}
                    
                    <div className="text-sm text-slate-500 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {evaluation.campaign?.selfEvalEndDate && (
                        <>Date limite: {new Date(evaluation.campaign.selfEvalEndDate).toLocaleDateString('fr-FR')}</>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">
                        {evaluation.objectives?.length || 0} objectif(s)
                      </span>
                      
                      {canSelfEval ? (
                        <Button size="sm" className={wasReturned ? 'bg-orange-600 hover:bg-orange-700' : 'bg-yellow-600 hover:bg-yellow-700'}>
                          <Edit3 className="w-4 h-4 mr-1" />
                          {wasReturned ? 'Reprendre mon auto-évaluation' : 'Faire mon auto-évaluation'}
                        </Button>
                      ) : isCompleted ? (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => downloadPDF(evaluation.id, e)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Télécharger PDF
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4 mr-1" />
                          Voir détails
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
